import axios from 'axios';
import { toCamelCase, toKebabCase, toSnakeCase } from 'case-converter';
import getConfig from 'next/config';
import { Router } from 'shared-routing';
import { Routes } from '@common/constants';
import { parseTopLevelDotKeys } from '@utils/helpers';
import { getToken } from './auth';

const services = {
  CATALOGUE: 'catalogue',
};
const nextConfig = getConfig();

let BACKEND_URL = 'http://localhost:5000';
let CATALOGUE_URL = '';
let SERVICE_URL = '';

if (nextConfig) {
  BACKEND_URL = nextConfig?.publicRuntimeConfig?.BACKEND_URL;
  CATALOGUE_URL = nextConfig?.publicRuntimeConfig?.CATALOGUE_URL;
  SERVICE_URL = nextConfig?.publicRuntimeConfig?.SERVICE_URL;
}

const serviceUrl = (type) => {
  if (type === services.CATALOGUE) {
    return CATALOGUE_URL ?? `${SERVICE_URL}/${type}`;
  }

  return `${SERVICE_URL}/${type}`;
};

export { serviceUrl };

export const ACCEPT_HEADER_V2 = 'application/vnd.onejourney.v2.1+json';

const parseError = (error) => {
  const { response = {} } = error;

  return {
    ...error,
    response: {
      ...response,
      data: response.data
        ? {
            ...toCamelCase(parseTopLevelDotKeys(response.data)),
            // eslint-disable-next-line dot-notation
            _error: response.data['_error'],
          }
        : null,
    },
  };
};

class Request {
  constructor() {
    this.initConfig();
    this.withUnauthorisedRedirect = true;
    this.store = this.createAxiosInstance(BACKEND_URL, 'application/vnd.onejourney.v2.1+json');
    this.catalogue = this.createAxiosInstance(serviceUrl(services.CATALOGUE));
  }

  disableUnauthorisedRedirect() {
    this.withUnauthorisedRedirect = false;

    return this;
  }

  initConfig() {
    this.instance = axios.create({
      baseURL: BACKEND_URL,
    });

    this.instance.defaults.params = {
      fallback: 1,
    };

    this.instance.interceptors.request.use(async (config) => {
      if (getToken()) {
        config.headers.Authorization = `Bearer ${getToken()}`;
      }

      config.data = config.data ? toSnakeCase(config.data) : config.data;
      config.params = config.params ? toKebabCase(config.params) : config.params;

      return config;
    });

    this.instance.interceptors.response.use(
      (response) => ({
        ...response,
        data: toCamelCase(response.data),
      }),
      this.handleAuthenticationError
    );
  }

  handleAuthenticationError = (error) => {
    if (this.withUnauthorisedRedirect && error.response && error.response.status === 401) {
      Router.push({ pathname: Routes.SIGNIN });
    }

    return Promise.reject(parseError(error));
  };

  createAxiosInstance(baseURL, acceptHeader = 'application/json') {
    const instance = axios.create({
      baseURL,
      headers: {
        Accept: acceptHeader,
      },
    });

    instance.interceptors.request.use(async (config) => {
      if (getToken()) {
        config.headers.Authorization = `Bearer ${getToken()}`;
      }

      return config;
    });

    instance.interceptors.response.use((response) => {
      return response;
    }, this.handleAuthenticationError);

    return instance;
  }

  get base() {
    this.baseInstance = this.baseInstance || this.createAxiosInstance(BACKEND_URL);

    return this.baseInstance;
  }

  get v2() {
    this.v2instance = this.v2instance || this.createAxiosInstance(BACKEND_URL, ACCEPT_HEADER_V2);

    return this.v2instance;
  }

  get(...args) {
    return this.instance.get(...args);
  }

  post(...args) {
    return this.instance.post(...args);
  }

  options(...args) {
    return this.instance.options(...args);
  }

  patch(...args) {
    return this.instance.patch(...args);
  }

  delete(...args) {
    return this.instance.delete(...args);
  }
}

export default new Request();
